'use strict'
import axios from 'axios';
import UsersApi from '@/api/users.js'
import { setComputedName, setFullName } from '@/_helpers/utils.js'

const state = {
  loading: false,
  error: null,
  list: null,
  listDropdown: null,
  recipients: null,
  selectedUsers: [],
  sorting: {
    name: null,
    order: 'asc'
  },
  search_term: null,
  role_id: []
}

const actions = {
  createUsers ({commit}, models) {
    return UsersApi
      .createUsers(models)
      .then(() => {
        commit('set_error', null);
        commit('set_loaded', false);
      })
      .catch(err => {
        console.warn('err', err)
        commit('set_error', err);
        commit('set_loaded', false);
        throw err
      })
  },

  fetchUsers ({ state, commit }) {
    commit('set_loaded', true);

    const { sorting, search_term, role_id } = state
    let params = {}

    if (sorting.name) {
      params['sort_by'] = sorting.name
      params['sort_order'] = sorting.order
    }

    if (search_term) {
      params['search_term'] = search_term
    }

    if (role_id.length > 0) {
      params['role_id'] = role_id
    }

    return UsersApi.fetchCompanyUsers(params)
      .then((users) => {
        commit('fetchUsersSuccess', users)
        commit('set_error', null);
        commit('set_loaded', false);
      })
      .catch(err => {
        commit('set_error', err);
        commit('set_loaded', false);
        throw err
      })
  },

  fetchUsersDropdown ({ state, commit }) {
    commit('set_loaded', true);

    const { sorting, search_term, role_id } = state
    let params = {}

    if (sorting.name) {
      params['sort_by'] = sorting.name
      params['sort_order'] = sorting.order
    }

    if (search_term) {
      params['search_term'] = search_term
    }

    if (role_id.length > 0) {
      params['role_id'] = role_id
    }

    return UsersApi.fetchCompanyUsersDropdown(params)
      .then((users) => {
        commit('fetchUsersDropdownSuccess', users)
        commit('set_error', null);
        commit('set_loaded', false);
      })
      .catch(err => {
        commit('set_error', err);
        commit('set_loaded', false);
        throw err
      })
  },

  fetchRecipients ({commit}) {
    commit('set_loaded', true);

    const sorting = state.sorting
    let params = {}
    if (sorting.name) {
      params['sort_by'] = sorting.name
      params['sort_order'] = sorting.order
    }

    return axios
      .get(`/v2/response-link/emails`)
      .then(r => {
        commit('fetchRecipientsSuccess', r.data);
        commit('set_loaded', false);
      })
      .catch(e => {
        commit('set_loaded', true);
        return Promise.reject(e)
      });
  },

  updateUsers ({commit, state, dispatch}, models) {
    commit('set_loaded', true);
    commit('updateUsers', models)
    return UsersApi
      .updateUsers(models)
      .then(() => {
        commit('set_error', null);
        commit('set_loaded', false);
      })
      .catch(err => {
        console.warn('err', err)
        commit('set_error', err);
        commit('set_loaded', false);
        throw err
      })
  },

  deleteUsers ({ commit, dispatch }, models) {
    return UsersApi
      .deleteUsers(models)
      .then(users => users.map(user => {
        commit('removeFromSelected', user)
        commit('removeFromList', user)
      }))
      .catch(err => {
        dispatch('global/setError', err, { root: true })
        commit('set_error', err);
        commit('set_loaded', false);
        throw err
      })
  },

  updateSorting ({commit, state, dispatch}, data) {
    commit('updateSorting', data)
    dispatch('fetchUsers')
  },

  updateSearchTerm ({ commit, state, dispatch }, search_term) {
    commit('updateSearchTerm', search_term)
    dispatch('fetchUsers')
  },

  updateRoleId ({ commit, state, dispatch }, idList) {
    commit('updateRoleId', idList)
    dispatch('fetchUsers')
  },

  setSelectedState ({commit}, {model, state}) {
    if (state) {
      commit('addToSelected', model)
    } else {
      commit('removeFromSelected', model)
    }
  }
}

const mutations = {
  set_loaded(state, val) {
    state.loading = val;
  },

  set_error(state, val) {
    state.error = val;
  },

  fetchUsersSuccess (state, users) {
    state.list = users
      .map(user => Object.assign({}, user, {user_id: user.id}))
      .map(user => setComputedName(user))
      .map(user => setFullName(user))
  },

  fetchUsersDropdownSuccess (state, users) {
    state.listDropdown = users
      .map(user => setComputedName(user, 'first_name', 'last_name'))
      .map(user => setFullName(user, 'first_name', 'last_name'))
  },

  fetchRecipientsSuccess (state, users) {
    state.recipients = users
      .map((user, index) => {
        return Object.assign({}, user, {
          id: `${new Date().getTime()}${index}`,
          computed_name: user.email
        })
      })
  },

  updateUsers (state, users) {
    state.list = state.list.map(_user => {
      const found = users.find(user => _user.email === user.email)
      if (found) {
        return Object.assign({}, found)
      }
      return _user
    })
  },

  updateSorting (state, data) {
    Object.assign(state.sorting, data)
  },

  updateSearchTerm (state, search_term) {
    state.search_term = search_term
  },

  updateRoleId (state, role_id) {
    state.role_id = role_id
  },

  addToSelected (state, user) {
    state.selectedUsers.push(user)
  },

  removeFromSelected (state, user) {
    state.selectedUsers = state.selectedUsers
      .filter(item => item.email !== user.email)
  },

  removeFromList (state, user) {
    state.list = state.list
      .filter(item => item.email !== user.email)
  },

  resetSelected (state) {
    state.selectedUsers = []
  }
}

const getters = {
  error (state, getters) {
    return state.error
  },

  loading (state, getters) {
    return state.loading
  },

  list (state, getters) {
    return state.list
  },

  listDropdown (state, getters) {
    return state.listDropdown
  },

  recipients (state, getters) {
    return state.recipients
  },

  selectedUsers: (state, getters) => {
    return state.selectedUsers
  },

  isSelected: (state, getters) => ({ email }) => {
    return state.selectedUsers.some(user => user.email === email)
  }

}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
  modules: {}
}
